import React, { useEffect, useState } from "react";
import { Crown, Loader2, Plus, ShieldCheck, Trash2, UserPlus } from "lucide-react";
import { toast } from "sonner";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Skeleton } from "../ui/skeleton";
import { errorMessage, teamApi } from "../../lib/api";
import { EMAIL_RE } from "../../lib/forms";
import { fmtDateTime } from "./SubmissionsTable";

const inputCls = "h-10 rounded-none border-border bg-transparent px-3 text-sm focus-visible:ring-1 focus-visible:ring-primary focus-visible:ring-offset-0";

const MemberRow = ({ m, canManage, onRemove, masterEmail }) => {
  const isMaster = m.role === "master" || m.email === masterEmail;
  return (
    <li className="flex flex-wrap items-center justify-between gap-3 px-4 py-3" data-testid={`team-member-${m.id}`}>
      <div className="flex min-w-0 items-center gap-3">
        {m.picture ? (
          <img src={m.picture} alt="" className="h-9 w-9 rounded-full border border-border" referrerPolicy="no-referrer" />
        ) : (
          <span className="flex h-9 w-9 items-center justify-center border border-border text-xs font-semibold uppercase">{(m.name || m.email).slice(0, 1)}</span>
        )}
        <div className="min-w-0">
          <p className="flex items-center gap-2 truncate text-sm font-semibold">
            {m.name || m.email}
            {isMaster && <Crown className="h-3.5 w-3.5 text-primary" title="Master admin" />}
          </p>
          <p className="truncate text-xs text-muted-foreground">{m.email}</p>
        </div>
      </div>
      <div className="flex items-center gap-3">
        <span className={`text-[10px] font-semibold uppercase tracking-[0.2em] ${m.status === "active" ? "text-emerald-400" : "text-muted-foreground"}`}>
          {m.status === "active" ? `Active · last sign-in ${fmtDateTime(m.last_login_at)}` : "Invited · hasn't signed in yet"}
        </span>
        {canManage && !isMaster && (
          <Button size="icon" variant="ghost" className="h-8 w-8 hover:text-destructive" onClick={() => onRemove(m)} aria-label={`Remove ${m.email}`} data-testid={`team-remove-${m.id}`}>
            <Trash2 className="h-4 w-4" />
          </Button>
        )}
      </div>
    </li>
  );
};

const MasterPendingRow = ({ email }) => (
  <li className="flex flex-wrap items-center justify-between gap-3 px-4 py-3" data-testid="team-master-pending">
    <div className="flex items-center gap-3">
      <span className="flex h-9 w-9 items-center justify-center border border-primary/50 text-primary">
        <Crown className="h-4 w-4" />
      </span>
      <div>
        <p className="text-sm font-semibold">Master admin</p>
        <p className="text-xs text-muted-foreground">{email}</p>
      </div>
    </div>
    <span className="text-[10px] font-semibold uppercase tracking-[0.2em] text-muted-foreground">Hasn't signed in with Google yet</span>
  </li>
);

const AddMemberForm = ({ onAdded }) => {
  const [form, setForm] = useState({ email: "", name: "" });
  const [busy, setBusy] = useState(false);
  const add = async (e) => {
    e.preventDefault();
    if (!EMAIL_RE.test(form.email)) return toast.error("Enter a valid Google email address");
    setBusy(true);
    try {
      const m = await teamApi.add(form.email.trim(), form.name.trim());
      onAdded(m);
      setForm({ email: "", name: "" });
      toast.success("Team member added", { description: `${m.email} can now sign in with Google.` });
    } catch (err) {
      toast.error(errorMessage(err));
    } finally {
      setBusy(false);
    }
  };
  return (
    <form onSubmit={add} className="grid gap-3 border border-dashed border-border p-4 sm:grid-cols-[1fr_1fr_auto] sm:items-end" data-testid="team-add-form">
      <div className="space-y-1.5">
        <Label className="text-[10px] font-semibold uppercase tracking-[0.2em] text-muted-foreground">Google email</Label>
        <Input type="email" value={form.email} onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))} placeholder="teammate@gmail.com" className={inputCls} data-testid="team-add-email" />
      </div>
      <div className="space-y-1.5">
        <Label className="text-[10px] font-semibold uppercase tracking-[0.2em] text-muted-foreground">Name (optional)</Label>
        <Input value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} placeholder="Jane" className={inputCls} data-testid="team-add-name" />
      </div>
      <Button type="submit" disabled={busy} className="h-10 rounded-none px-5 text-[11px] font-semibold uppercase tracking-[0.2em]" data-testid="team-add-submit">
        {busy ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <UserPlus className="mr-2 h-4 w-4" />} Add
      </Button>
    </form>
  );
};

/** Who can sign in with Google. Only the master admin can add or remove people. */
const TeamAccess = ({ canManage }) => {
  const [data, setData] = useState(null);

  useEffect(() => {
    teamApi
      .list()
      .then(setData)
      .catch((err) => toast.error(errorMessage(err, "Could not load team")));
  }, []);

  const remove = async (m) => {
    try {
      await teamApi.remove(m.id);
      setData((d) => ({ ...d, members: d.members.filter((x) => x.id !== m.id) }));
      toast.success("Access removed", { description: m.email });
    } catch (err) {
      toast.error(errorMessage(err));
    }
  };

  const masterListed = data?.members.some((m) => m.email === data.master_email);

  return (
    <section className="border border-border bg-card" data-testid="team-access">
      <header className="flex items-center gap-2 border-b border-border px-6 py-4 text-sm font-semibold">
        <ShieldCheck className="h-4 w-4 text-primary" /> Team access (Google sign-in)
      </header>
      <div className="space-y-5 px-6 py-5">
        <p className="text-sm text-muted-foreground">
          Only the master admin and the people listed here can sign in with Google. {canManage ? "Add a teammate's Google email below; they can sign in immediately." : "Ask the master admin to add or remove people."}
        </p>

        {data === null ? (
          <Skeleton className="h-24" />
        ) : (
          <ul className="divide-y divide-border border border-border" data-testid="team-list">
            {!masterListed && data.master_email && <MasterPendingRow email={data.master_email} />}
            {data.members.map((m) => (
              <MemberRow key={m.id} m={m} canManage={canManage} onRemove={remove} masterEmail={data.master_email} />
            ))}
          </ul>
        )}

        {canManage && <AddMemberForm onAdded={(m) => setData((d) => ({ ...d, members: [...d.members, m] }))} />}
      </div>
    </section>
  );
};

export default TeamAccess;
